/**
 * Elite-tab lock-in price card (assurance divider + benefits + art + links).
 *
 * Belongs here: presentational card + pricing-details / philosophy clicks.
 * Does not belong here: fee resolution — pass live price props from the hero.
 */
import { useState } from 'react'
import { ElitePlansEventId, trackElitePlansClick } from '../../../analytics'
import { ELITE_PLANS_PRICING_PHILOSOPHY_URL } from '../../../constants/assets'
import { ChevronRightIcon } from '../../../icons/ChevronRightIcon'
import { LowestPriceAssurance } from '../plus-banner/LowestPriceAssurance'
import type { LockInPriceBanner } from '../../../types/lockInPriceBanner.types'
import VaulDrawer from '../../../../../components/BottomSheetComponents/VaulDrawer'
import { openInBrowser } from '../../../../../utils/appBridge'
import { elitePlansPricingDetailsTitleId } from '../../ElitePlansPricingDetailsBottomSheet'
import { ElitePlansElitePricingDetailsBottomSheet } from './ElitePlansElitePricingDetailsBottomSheet'

const SPARKLE_SRC = '/images/elite-plans/elite/lock-in-price/sparkle.svg'

/**
 * Same fee keys as `OFFER_STRIP_BY_PRICE` in EliteLandingJoinOfferStrip —
 * per-day copy for the lock-in card’s first bullet.
 */
const PER_DAY_BULLET_BY_FEE: Record<number, string> = {
  499: 'Less than ₹2 per day',
  749: 'Less than ₹3 per day',
  999: 'Less than ₹4 per day',
  1249: 'Less than ₹4 per day',
  1499: 'Less than ₹5 per day'
}

export function ElitePlansLockInPriceBanner({
  planId,
  banner,
  priceLabel,
  sheetPrice,
  sheetPriceSuffix,
  feeAmount
}: {
  planId: string
  banner: LockInPriceBanner
  /** Live fee label with tenure suffix, e.g. "₹999/yr". Empty hides the accent. */
  priceLabel: string
  /** Formatted fee for the pricing sheet headline, e.g. "₹999". */
  sheetPrice: string
  /** Suffix after sheet price, e.g. "/yr+GST". */
  sheetPriceSuffix: string
  feeAmount: number
}) {
  const [isPricingDetailsOpen, setIsPricingDetailsOpen] = useState(false)
  const detailsLabel = banner.detailsLabel ?? ''
  const philosophyLabel = banner.philosophyLabel ?? ''
  const artAlt = banner.artAlt ?? ''
  const bullets = resolveLockInBullets(banner.bullets ?? [], feeAmount)
  const pricingDetails = banner.pricingDetails

  const handleViewPricingDetails = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId,
      buttonLabel: detailsLabel || 'Pricing details'
    })
    if (pricingDetails) {
      setIsPricingDetailsOpen(true)
    }
  }

  const handlePhilosophy = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId,
      buttonLabel: philosophyLabel || 'Pricing philosophy'
    })
    openInBrowser(ELITE_PLANS_PRICING_PHILOSOPHY_URL)
  }

  return (
    <section className='mt-6 flex w-full flex-col items-center gap-4 text-left'>
      <LowestPriceAssurance title={banner.assuranceTitle} />

      <div className='flex w-full flex-col items-center gap-1'>
        <div className='relative w-full overflow-hidden rounded-xl border-[1.5px] border-solid border-white/50 bg-[color:var(--ep-plus-card)]'>
          <div className='relative z-[1] flex items-center p-4 pr-[132px]'>
            <div className='flex min-w-0 flex-col gap-4'>
              <h3 className='m-0 text-left text-base font-medium leading-[1.4] text-[#232323]'>
                <span className='block'>{banner.title}</span>
                {priceLabel ? (
                  <span className='block text-[#cc6e30]'>
                    {banner.pricePrefix}
                    {priceLabel}
                  </span>
                ) : null}
              </h3>

              {bullets.length > 0 ? (
                <ul className='m-0 flex list-none flex-col gap-1 p-0'>
                  {bullets.map((bullet) => (
                    <li key={bullet} className='flex items-center gap-1'>
                      <img
                        src={SPARKLE_SRC}
                        alt=''
                        aria-hidden='true'
                        width={8}
                        height={8}
                        className='size-2 shrink-0'
                      />
                      <span className='text-[12px] font-normal leading-[1.5] text-[#7f430e] opacity-80'>
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <img
              src={banner.artSrc}
              alt={artAlt}
              aria-hidden={artAlt ? undefined : true}
              className='pointer-events-none absolute bottom-0 right-0 z-0 h-[148px] w-[145px] object-contain object-top'
            />
          </div>

          {detailsLabel ? (
            <button
              type='button'
              onClick={handleViewPricingDetails}
              className='ep-focus-ring relative z-[1] flex h-12 w-full items-center justify-between border-0 border-t border-solid border-[#ebf2f2] bg-transparent px-4 py-0 text-left'
              aria-label={detailsLabel}
            >
              <span className='text-[13px] font-medium leading-[1.5] text-grey10'>{detailsLabel}</span>
              <ChevronRightIcon size={10} color='var(--ep-plus-link)' className='shrink-0 opacity-70' />
            </button>
          ) : null}
        </div>

        {philosophyLabel ? (
          <button
            type='button'
            onClick={handlePhilosophy}
            className='ep-focus-ring inline-flex min-h-11 items-center gap-1 border-none bg-transparent px-3.5 py-0'
            aria-label={philosophyLabel}
          >
            <span className='text-[13px] font-medium leading-[1.3] text-[#732e17]'>{philosophyLabel}</span>
            <ChevronRightIcon size={10} color='#732e17' className='shrink-0' />
          </button>
        ) : null}
      </div>

      {pricingDetails ? (
        <VaulDrawer
          open={isPricingDetailsOpen}
          setOpen={setIsPricingDetailsOpen}
          titleId={elitePlansPricingDetailsTitleId}
          className='!bg-[#131d28]'
          focusDrawerOnOpen
        >
          <ElitePlansElitePricingDetailsBottomSheet
            open={isPricingDetailsOpen}
            template={pricingDetails}
            price={sheetPrice}
            priceSuffix={sheetPriceSuffix}
            feeAmount={feeAmount}
            onClose={() => setIsPricingDetailsOpen(false)}
          />
        </VaulDrawer>
      ) : null}
    </section>
  )
}

/** Prepends the fee-keyed per-day line when the live fee matches a known offer price. */
function resolveLockInBullets(cmsBullets: string[], feeAmount: number): string[] {
  const perDayBullet = PER_DAY_BULLET_BY_FEE[feeAmount]
  if (!perDayBullet) return cmsBullets
  return [perDayBullet, ...cmsBullets]
}
